import { DataTableColumn } from "mantine-datatable";
import moment from "moment";
import { useCallback, useMemo } from "react";

import ActionIcon from "../../../components/ActionIcon/ActionIcon.tsx";
import { DatabaseMetadataPopover } from "../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import SvgEdit from "../../../components/Icons/Edit.tsx";
import Spoiler from "../../../components/Spoiler/Spoiler.tsx";
import { TableData, TableRow } from "../../../components/Table/types.ts";
import TableFilesDisplay from "../../../components/TableFilesDisplay/TableFilesDisplay.tsx";
import { TradeInsurance } from "../../../types/tradeInsurance.ts";
import { toLocalDate } from "../../../utils/date.ts";
import { toCurrencyPrice } from "../../../utils/number.ts";
import { toHumanReadable } from "../../../utils/string.ts";

interface Props {
  data: TradeInsurance[] | null | undefined;
  onChange: (id: number) => void;
}

export function useTradeInsuranceTableData({
  data,
  onChange,
}: Props): TableData {
  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "insuranceName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TradeInsurance",
            fieldName: "insuranceName",
          }}
        >
          Insurance Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "250px",
    },
    {
      accessor: "customerName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{ tableName: "Customer", fieldName: "name" }}
        >
          Customer Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "250px",
    },
    {
      accessor: "requestedAmount",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TradeInsurance",
            fieldName: "requestedAmount",
          }}
        >
          Requested Trade Insurance Amount
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "approvedAmount",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TradeInsurance",
            fieldName: "approvedAmount",
          }}
        >
          Approved Trade Insurance Amount
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "limitAmount",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TradeInsurance",
            fieldName: "limitAmount",
          }}
        >
          Bababos Limit Amount
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "requestedDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TradeInsurance",
            fieldName: "requestedDate",
          }}
        >
          Requested Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "resultDate",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TradeInsurance",
            fieldName: "resultDate",
          }}
        >
          Result Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "sla",
      title: "SLA",
    },
    {
      accessor: "status",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TradeInsurance",
            fieldName: "status",
          }}
        >
          Status
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "file",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TradeInsurance",
            fieldName: "file",
          }}
        >
          Trade Insurance File
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "grade",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TradeInsurance",
            fieldName: "grade",
          }}
        >
          Grade
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "250px",
    },
    {
      accessor: "creditLimitFees",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TradeInsurance",
            fieldName: "creditLimitFees",
          }}
        >
          Credit Limit Fees
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: "250px",
    },
    {
      accessor: "transactionsNumber",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TradeInsurance",
            fieldName: "transactionsNumber",
          }}
        >
          # of Transactions
        </DatabaseMetadataPopover>
      ),
    },
    {
      accessor: "notes",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "TradeInsurance",
            fieldName: "notes",
          }}
        >
          Notes
        </DatabaseMetadataPopover>
      ),
      width: "350px",
    },
    {
      accessor: "actions",
      title: "Actions",
    },
  ];

  const handleOnChange = useCallback(
    (id: number) => {
      onChange(id);
    },
    [onChange]
  );

  const rows = useMemo(() => {
    return data?.map((item) => {
      return {
        insuranceName: item.insuranceName,
        customerName: item.customer.name,
        requestedAmount: toCurrencyPrice(item.requestedAmount),
        approvedAmount: toCurrencyPrice(item.approvedAmount),
        limitAmount: toCurrencyPrice(item.limitAmount),
        requestedDate: toLocalDate(item.requestedDate),
        resultDate: toLocalDate(item.resultDate),
        sla:
          item.resultDate && item.requestedDate
            ? moment(item.resultDate).diff(moment(item.requestedDate), "days")
            : undefined,
        file: item.file ? <TableFilesDisplay files={[item.file]} /> : null,
        status: toHumanReadable(item.status),
        grade: item.grade,
        creditLimitFees: toCurrencyPrice(item.creditLimitFees),
        transactionsNumber: item.transactionsNumber,
        notes: item.notes ? (
          <Spoiler maxStringLength={300} text={item.notes} />
        ) : undefined,
        actions: (
          <ActionIcon onClick={() => handleOnChange(item.id)}>
            <SvgEdit />
          </ActionIcon>
        ),
      };
    });
  }, [data, handleOnChange]);
  return [columns, rows];
}
